/**
 * Die Verlagsschlüssel — anlegen, ansehen, widerrufen (`W2`, **E135**).
 *
 * Ein Verlagsschlüssel öffnet auf der Startseite die Spielanleitung samt Erklärfilm, sonst nichts.
 * Der User legt einen je Verlag an und gibt ihn mit der Adresse der Seite weiter.
 *
 * **Gebaut wie `HostTokenList.tsx`, und aus denselben Gründen:** der Klartext erscheint genau
 * einmal und wird nirgends abgelegt; widerrufen ist nicht löschen, die Zeile bleibt mit dem
 * Zeitpunkt stehen. Anders als ein Host-Link ist der Schlüssel **kein Link** — der Verlag tippt
 * oder fügt ihn in das Feld der Startseite ein, damit er in keiner Adresse steht.
 *
 * **Seit `W3` (E136) steht beim Schlüssel, was der Verlag getan hat**: wie oft freigeschaltet und
 * geöffnet, ob und wie weit der Film lief, bis zu welchem Kapitel gelesen wurde — und aufklappbar der
 * Verlauf. „Gelesen bis“ heißt nur „bis dorthin gescrollt“, und ein Schlüssel kann mehrere Menschen
 * meinen; die Anzeige sagt deshalb „bis“, nicht „gelesen“ als Urteil.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  PublisherKeyActivityDto,
  PublisherKeyCreatedDto,
  PublisherKeyDto,
  PublisherKeyEventDto,
} from '@flaghex/contracts';
import type { TFunction } from 'i18next';

import { useRequest } from '../api/context.js';
import { createPublisherKey, publisherKeyEvents, publisherKeys, revokePublisherKey } from '../api/endpoints.js';
import type { ApiFailure, ApiResult } from '../api/transport.js';

export interface PublisherKeyListProps {
  readonly adminKey: string;
  readonly onUnauthenticated: () => void;
}

/** Was die Angaben eines Eintrags trennt — wie bei den Host-Links. */
const SEPARATOR = ' · ';

function when(iso: string): string {
  return new Date(iso).toLocaleString('de-DE');
}

/** Sekunden als „m:ss“, wie die Kapitelmarken des Films. */
export function clock(seconds: number): string {
  const s = Math.floor(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Die Zusammenfassung eines Schlüssels als Teile, die die Liste mit Trennzeichen verbindet. */
function activityParts(activity: PublisherKeyActivityDto, t: TFunction): readonly string[] {
  const nothing =
    activity.unlocks === 0 && activity.opens === 0 && activity.filmStarts === 0 && activity.readFurthest === undefined;
  if (nothing) return [t('admin:publisherKeys.activity.none')];
  // Ein Schlüssel, der vor `W3` freigeschaltet wurde, öffnet über sein Cookie weiter: dann gibt es
  // Öffnen und Film, aber kein gezähltes Freischalten (gefunden in der Sichtprüfung zu W3).
  const parts: string[] = [];
  if (activity.unlocks > 0 && activity.lastUnlockAt !== undefined) {
    parts.push(t('admin:publisherKeys.activity.unlocks', { count: activity.unlocks, when: when(activity.lastUnlockAt) }));
  }
  parts.push(t('admin:publisherKeys.activity.opens', { count: activity.opens }));
  if (activity.filmStarts > 0) {
    parts.push(
      activity.filmFurthest !== undefined && activity.filmLength !== undefined
        ? t('admin:publisherKeys.activity.filmFurthest', {
            count: activity.filmStarts,
            at: clock(activity.filmFurthest),
            of: clock(activity.filmLength),
          })
        : t('admin:publisherKeys.activity.film', { count: activity.filmStarts }),
    );
  }
  if (activity.readFurthest !== undefined && activity.readOf !== undefined) {
    parts.push(t('admin:publisherKeys.activity.read', { n: activity.readFurthest, of: activity.readOf }));
  }
  return parts;
}

/** Ein Eintrag im Verlauf, für Menschen. */
function eventText(event: PublisherKeyEventDto, t: TFunction): string {
  const at = event.value === undefined ? '' : clock(event.value);
  return t(`admin:publisherKeys.history.${event.kind}`, { at, n: event.value });
}

/** Der aufklappbare Verlauf eines Schlüssels — geholt erst beim Aufklappen. */
function History({ adminKey, id, onFailure }: { readonly adminKey: string; readonly id: string; readonly onFailure: (failure: ApiFailure) => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const request = useRequest();
  const [events, setEvents] = useState<readonly PublisherKeyEventDto[] | undefined>(undefined);
  const [open, setOpen] = useState(false);

  const toggle = (): void => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    void publisherKeyEvents(request, adminKey, id).then((result) => {
      if (result.kind === 'ok') setEvents(result.value);
      else onFailure(result);
    });
  };

  return (
    <>
      {' '}
      <button type="button" onClick={toggle} aria-expanded={open}>
        {open ? t('admin:publisherKeys.history.hide') : t('admin:publisherKeys.history.show')}
      </button>
      {!open ? null : events === undefined ? (
        <p role="status">{t('common:loading')}</p>
      ) : events.length === 0 ? (
        <p>{t('admin:publisherKeys.history.empty')}</p>
      ) : (
        <ol>
          {events.map((event, index) => (
            <li key={`${event.at}-${index}`}>
              <span>{when(event.at)}</span>
              {SEPARATOR}
              <span>{eventText(event, t)}</span>
            </li>
          ))}
        </ol>
      )}
    </>
  );
}

export function PublisherKeyList({ adminKey, onUnauthenticated }: PublisherKeyListProps) {
  const { t } = useTranslation(['admin', 'errors', 'common']);
  const request = useRequest();

  const [keys, setKeys] = useState<readonly PublisherKeyDto[] | undefined>(undefined);
  const [label, setLabel] = useState('');
  const [created, setCreated] = useState<PublisherKeyCreatedDto | undefined>(undefined);
  const [failure, setFailure] = useState<ApiFailure | undefined>(undefined);

  /** Eine Abweisung führt zurück zur Schlüsseleingabe, jeder andere Fehler wird gezeigt. */
  const refused = useCallback(
    (result: ApiFailure): void => {
      if (result.code === 'UNAUTHENTICATED') onUnauthenticated();
      else setFailure(result);
    },
    [onUnauthenticated],
  );

  const load = useCallback(async (): Promise<void> => {
    const result = await publisherKeys(request, adminKey);
    if (result.kind === 'ok') setKeys(result.value);
    else refused(result);
  }, [request, adminKey, refused]);

  useEffect(() => {
    void load();
  }, [load]);

  const after = useCallback(
    async <T,>(result: ApiResult<T>, onOk: (value: T) => void): Promise<void> => {
      if (result.kind !== 'ok') {
        refused(result);
        return;
      }
      onOk(result.value);
      await load();
    },
    [load, refused],
  );

  const create = (): void => {
    const trimmed = label.trim();
    if (trimmed === '') return;
    setFailure(undefined);
    void createPublisherKey(request, adminKey, trimmed).then((result) =>
      after(result, (value) => {
        setCreated(value);
        setLabel('');
      }),
    );
  };

  const revoke = (id: string): void => {
    setFailure(undefined);
    void revokePublisherKey(request, adminKey, id).then((result) => after(result, () => undefined));
  };

  return (
    <section>
      <h2>{t('admin:publisherKeys.heading')}</h2>
      <p>{t('admin:publisherKeys.intro')}</p>

      <p>
        <label>
          {t('admin:publisherKeys.label')}{' '}
          <input type="text" value={label} onChange={(event) => setLabel(event.target.value)} />
        </label>{' '}
        <button type="button" onClick={create} disabled={label.trim() === ''}>
          {t('admin:publisherKeys.create')}
        </button>
      </p>

      {created === undefined ? null : (
        <div role="alert">
          <h3>{t('admin:publisherKeys.createdHeading')}</h3>
          <p>
            <code>{created.key}</code>
          </p>
          <p>{t('admin:publisherKeys.createdHint')}</p>
        </div>
      )}

      {failure === undefined ? null : <p role="alert">{t(`errors:${failure.code}`)}</p>}

      {keys === undefined ? (
        <p role="status">{t('common:loading')}</p>
      ) : keys.length === 0 ? (
        <p>{t('admin:publisherKeys.none')}</p>
      ) : (
        <ul>
          {keys.map((entry) => (
            <li key={entry.id}>
              <span>{entry.label}</span>
              {SEPARATOR}
              <span>{t('admin:publisherKeys.createdAt', { when: when(entry.createdAt) })}</span>
              {SEPARATOR}
              <span>
                {entry.lastUsedAt === undefined
                  ? t('admin:publisherKeys.neverUsed')
                  : t('admin:publisherKeys.lastUsedAt', { when: when(entry.lastUsedAt) })}
              </span>
              {SEPARATOR}
              {entry.revokedAt === undefined ? (
                <button type="button" onClick={() => revoke(entry.id)}>
                  {t('admin:publisherKeys.revoke')}
                </button>
              ) : (
                <span>{t('admin:publisherKeys.revoked', { when: when(entry.revokedAt) })}</span>
              )}
              <br />
              <span data-activity>{activityParts(entry.activity, t).join(SEPARATOR)}</span>
              <History adminKey={adminKey} id={entry.id} onFailure={refused} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
